import { z } from 'zod';
import { ProductTypeSchema } from './trading';

/**
 * Reporting contracts — the trade book and the realized-P&L statement.
 *
 * `JournalStats` (account.ts) already summarises trading DAY BY DAY; these
 * are the two views a trader needs underneath it: every individual fill, and
 * P&L netted per symbol with the charges that were actually debited.
 */

/** One executed leg, exactly as the engine booked it. */
export const TradeSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  symbol: z.string(),
  side: z.enum(['BUY', 'SELL']),
  product: ProductTypeSchema,
  qty: z.number(),
  price: z.number(),
  /** Turnover for the leg (qty × price). */
  value: z.number(),
  /** What the server actually debited for this leg. */
  charges: z.number(),
  at: z.number(),
});
export type Trade = z.infer<typeof TradeSchema>;

export const TradeBookSchema = z.object({
  trades: z.array(TradeSchema),
  /** Cursor for the next page; null when the last page has been served. */
  nextBefore: z.string().nullable(),
});
export type TradeBook = z.infer<typeof TradeBookSchema>;

/** Realized P&L for one symbol over the requested window. */
export const PnlRowSchema = z.object({
  symbol: z.string(),
  product: ProductTypeSchema,
  /** Quantity bought and sold within the window. */
  buyQty: z.number(),
  sellQty: z.number(),
  buyValue: z.number(),
  sellValue: z.number(),
  charges: z.number(),
  /** Net of charges — what actually hit the book. */
  realizedPnl: z.number(),
});
export type PnlRow = z.infer<typeof PnlRowSchema>;

export const PnlReportSchema = z.object({
  /** Inclusive ISO dates (IST session days) the report covers. */
  from: z.string(),
  to: z.string(),
  rows: z.array(PnlRowSchema),
  totals: z.object({
    turnover: z.number(),
    charges: z.number(),
    realizedPnl: z.number(),
    tradeCount: z.number(),
  }),
});
export type PnlReport = z.infer<typeof PnlReportSchema>;
